/*
 * MFA service
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import { Injectable } from '@angular/core';
import { Constants } from 'app/shared/config/constants';

export interface PendingMfaSession {
  mfaToken: string;
  redirectPath?: string | null;
  returnUrl?: string | null;
  urlSegmentKey?: string | null;
  authRedirectApp?: string | null;
}

@Injectable ( {
  providedIn: 'root',
} )
export class MfaService {

  public save ( session: PendingMfaSession ): void {
    localStorage.setItem ( Constants.localStorageKeys.reporterPendingMfa, this.encode ( session ) );
  }

  public read (): PendingMfaSession | null {
    const rawValue = localStorage.getItem ( Constants.localStorageKeys.reporterPendingMfa );

    if ( !rawValue ) {
      return null;
    }

    try {
      const pendingSession = this.decode ( rawValue );

      if ( !pendingSession || typeof pendingSession.mfaToken !== 'string' || !pendingSession.mfaToken ) {
        return null;
      }

      return pendingSession;
    } catch ( e ) {
      return null;
    }
  }

  public clear (): void {
    localStorage.removeItem ( Constants.localStorageKeys.reporterPendingMfa );
  }

  public redirectToMfa ( url?: string | null ): boolean {
    if ( !url || !this.isValidRedirectUrl ( url ) ) {
      return false;
    }

    this.assignLocation ( url );
    return true;
  }

  protected assignLocation ( url: string ): void {
    window.location.assign ( url );
  }

  private isValidRedirectUrl ( url: string ): boolean {
    try {
      return new URL ( url ).protocol === 'https:';
    } catch ( e ) {
      return false;
    }
  }

  // Base64 encode/decode with UTF-8 safety, since btoa/atob only support Latin-1
  // and the preserved navigation context can contain arbitrary Unicode characters.
  private encode ( session: PendingMfaSession ): string {
    const utf8Bytes = new TextEncoder ().encode ( JSON.stringify ( session ) );
    let binary = '';

    utf8Bytes.forEach ( ( byte ) => {
      binary += String.fromCharCode ( byte );
    } );

    return btoa ( binary );
  }

  private decode ( rawValue: string ): PendingMfaSession {
    const binary = atob ( rawValue );
    const bytes = new Uint8Array ( binary.length );

    for ( let i = 0; i < binary.length; i++ ) {
      bytes[ i ] = binary.charCodeAt ( i );
    }

    return JSON.parse ( new TextDecoder ().decode ( bytes ) ) as PendingMfaSession;
  }
}

